import {HttpInterceptorFn} from '@angular/common/http';
import {isHoomvipApiRequest} from './http-api-scope';

const ACTIVE_REQUEST_TTL_MS = 60_000;
const STORAGE_KEY = 'hoomvip-api-request-count-expirations';

let cleanupTimer: number | null = null;
let currentCount = 0;

function readExpirations(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > Date.now());
  } catch {
    return [];
  }
}

function writeExpirations(expirations: number[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(expirations));
}

function logCount(): void {
  console.log(`[Hoomvip API] Active requests in the last 60 seconds: ${currentCount}`);
}

function syncCountFromStorage(): void {
  const expirations = readExpirations();
  currentCount = expirations.length;
  writeExpirations(expirations);
}

function scheduleCleanup(): void {
  if (cleanupTimer !== null) {
    clearTimeout(cleanupTimer);
    cleanupTimer = null;
  }

  const expirations = readExpirations();
  if (expirations.length === 0) {
    syncCountFromStorage();
    logCount();
    return;
  }

  const nextExpiry = Math.min(...expirations);
  const delay = Math.max(0, nextExpiry - Date.now());

  cleanupTimer = window.setTimeout(() => {
    const now = Date.now();
    const updatedExpirations = readExpirations().filter((expiry) => expiry > now);

    writeExpirations(updatedExpirations);

    const newCount = updatedExpirations.length;
    if (newCount !== currentCount) {
      currentCount = newCount;
      logCount();
    }

    scheduleCleanup();
  }, delay + 1);
}

function addRequest(): void {
  const expirations = readExpirations();
  const newExpiry = Date.now() + ACTIVE_REQUEST_TTL_MS;

  expirations.push(newExpiry);
  writeExpirations(expirations);

  currentCount = expirations.length;
  logCount();

  scheduleCleanup();
}

syncCountFromStorage();
scheduleCleanup();

export const hoomvipRequestCountInterceptor: HttpInterceptorFn = (req, next) => {
  if (isHoomvipApiRequest(req.url)) {
    addRequest();
    console.log(`[Hoomvip API] New request: ${req.url}`);
  }

  return next(req);
};
